import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as PackageIcon,
  PlayArrow as UseIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import PackageService from '../../services/PackageService';

const PatientPackageManager = ({ patientId }) => {
  const [availablePackages, setAvailablePackages] = useState([]);
  const [patientPackages, setPatientPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [usingSession, setUsingSession] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const [packages, patientPkgs] = await Promise.all([
        PackageService.getAllPackages(),
        PackageService.getPatientPackages(patientId)
      ]);
      
      setAvailablePackages(packages.filter(pkg => pkg.isActive));
      setPatientPackages(patientPkgs);
    } catch (err) {
      setError('Failed to load package data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      loadData();
    }
  }, [patientId, loadData]);

  const handlePurchasePackage = async () => {
    if (!selectedPackage) return;

    setPurchasing(true);
    setError('');

    try {
      await PackageService.purchasePackage(patientId, selectedPackage.id);
      setSuccess(`Successfully purchased ${selectedPackage.name}!`);
      setShowPurchaseDialog(false);
      setSelectedPackage(null);
      await loadData(); // Reload data to show new purchase
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to purchase package');
      console.error('Error purchasing package:', err);
    } finally {
      setPurchasing(false);
    }
  };

  const handleUseSession = async () => {
    if (!selectedPackage) return;

    setUsingSession(true);
    setError('');

    try {
       await PackageService.consumePackageSession(selectedPackage.id);
       setSuccess(`Session used successfully for ${selectedPackage.packageName}!`);
       setShowUseDialog(false);
       setSelectedPackage(null);
       await loadData(); // Reload data to show updated session count
     } catch (err) {
      setError(err.response?.data?.message || 'Failed to use session');
      console.error('Error using session:', err);
    } finally {
      setUsingSession(false);
    }
  };

  const handleViewHistory = async (patientPackage) => {
    setLoading(true);
    setError('');

    try {
      const history = await PackageService.getSessionUsageHistory(patientPackage.id);
      setSessionHistory(history);
      setSelectedPackage(patientPackage);
      setShowHistoryDialog(true);
    } catch (err) {
      setError('Failed to load session history');
      console.error('Error loading history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSessionProgress = (used, total) => {
    return total > 0 ? (used / total) * 100 : 0;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'error';
    if (percentage >= 60) return 'warning';
    return 'primary';
  };

  if (loading && patientPackages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <PackageIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Patient Package Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Available Packages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Packages
              </Typography>
              
              {availablePackages.length === 0 ? (
                <Typography color="text.secondary">
                  No packages available for purchase.
                </Typography>
              ) : (
                <List>
                  {availablePackages.map((pkg) => (
                    <ListItem key={pkg.id} divider>
                      <ListItemText
                        primary={pkg.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {pkg.description}
                            </Typography>
                            <Box display="flex" gap={1} mt={1}>
                              <Chip 
                                label={formatCurrency(pkg.price)} 
                                color="primary" 
                                size="small" 
                              />
                              <Chip 
                                label={`${pkg.totalSessions + pkg.freeSessions} sessions`} 
                                variant="outlined" 
                                size="small" 
                              />
                              {pkg.freeSessions > 0 && (
                                <Chip 
                                  label={`+${pkg.freeSessions} free`} 
                                  color="success" 
                                  size="small" 
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ShoppingCartIcon />}
                          onClick={() => {
                            setSelectedPackage(pkg);
                            setShowPurchaseDialog(true);
                          }}
                        >
                          Purchase
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Patient's Packages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Patient's Packages
              </Typography>
              
              {patientPackages.length === 0 ? (
                <Typography color="text.secondary">
                  No packages purchased yet.
                </Typography>
              ) : (
                <List>
                  {patientPackages.map((patientPkg) => {
                    const progress = getSessionProgress(patientPkg.usedSessions, patientPkg.totalSessions);
                    const progressColor = getProgressColor(progress);
                    
                    return (
                      <ListItem key={patientPkg.id} divider>
                        <ListItemText
                          primary={patientPkg.packageName}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Purchased: {formatDate(patientPkg.purchaseDate)}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <Typography variant="caption">
                                  {patientPkg.usedSessions} / {patientPkg.totalSessions} sessions used
                                </Typography>
                                <Box sx={{ flexGrow: 1, mx: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={progress} 
                                    color={progressColor}
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                </Box>
                                <Typography variant="caption" color={`${progressColor}.main`}>
                                  {Math.round(progress)}%
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Use Session">
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={patientPkg.usedSessions >= patientPkg.totalSessions}
                                onClick={() => {
                                  setSelectedPackage(patientPkg);
                                  setShowUseDialog(true);
                                }}
                              >
                                <UseIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View History">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleViewHistory(patientPkg)}
                              >
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseDialog} onClose={() => setShowPurchaseDialog(false)}>
        <DialogTitle>Confirm Package Purchase</DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPackage.name}
              </Typography>
              <Typography color="text.secondary" paragraph>
                {selectedPackage.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Price:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(selectedPackage.price)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Sessions:
                  </Typography>
                  <Typography variant="h6">
                    {selectedPackage.totalSessions + selectedPackage.freeSessions}
                  </Typography>
                </Grid>
              </Grid>
              {selectedPackage.freeSessions > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <InfoIcon sx={{ mr: 1 }} />
                  Includes {selectedPackage.freeSessions} free bonus sessions!
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPurchaseDialog(false)} disabled={purchasing}>
            Cancel
          </Button>
          <Button
            onClick={handlePurchasePackage}
            variant="contained"
            disabled={purchasing}
            startIcon={purchasing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {purchasing ? 'Processing...' : 'Confirm Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use Session Confirmation Dialog */}
      <Dialog open={showUseDialog} onClose={() => setShowUseDialog(false)}>
        <DialogTitle>Use Package Session</DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPackage.packageName}
              </Typography>
              <Typography color="text.secondary" paragraph>
                Are you sure you want to use one session from this package?
              </Typography>
              <Alert severity="warning">
                This action cannot be undone. The session will be marked as used.
              </Alert>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Remaining Sessions: {selectedPackage.totalSessions - selectedPackage.usedSessions}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUseDialog(false)} disabled={usingSession}>
            Cancel
          </Button>
          <Button
            onClick={handleUseSession}
            variant="contained"
            disabled={usingSession}
            startIcon={usingSession ? <CircularProgress size={20} /> : <UseIcon />}
          >
            {usingSession ? 'Processing...' : 'Use Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session History Dialog */}
      <Dialog 
        open={showHistoryDialog} 
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Session Usage History</DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPackage.packageName}
              </Typography>
              
              {sessionHistory.length === 0 ? (
                <Typography color="text.secondary">
                  No sessions have been used yet.
                </Typography>
              ) : (
                <List>
                  {sessionHistory.map((session, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={`Session ${index + 1}`}
                        secondary={formatDate(session.usedDate)}
                      />
                      <ListItemSecondaryAction>
                        <Chip 
                          label="Used" 
                          color="success" 
                          size="small" 
                          icon={<CheckIcon />}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientPackageManager;